/**
 * EmployeeAutocomplete Component
 * 
 * A typeahead autocomplete dropdown for selecting employees from lt_employee.
 * Features:
 * - Real-time search as you type
 * - Shows emp_code and emp_name
 * - Filters by any character in name/code
 * - Optional: prioritize project employees
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const EmployeeAutocomplete = ({ 
  value,
  onChange,
  projectId,
  placeholder = 'Search employee...',
  disabled = false,
  required = false,
  style = {},
  /** When true, only active employees with feeds_indirect (Indirect Resources). */
  indirectOnly = false,
  /** Extra query string, e.g. "" — reserved for future filters */
  searchExtra = '',
}) => {
  const { instance, accounts } = useMsal();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Set input value when value prop changes
  useEffect(() => {
    if (value) {
      setInputValue(value.emp_name || value.empl_name || `${value.emp_code || value.empl_code || ''}`);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      let url = `/lt-employee/search?q=${encodeURIComponent(query)}&limit=20`;
      if (indirectOnly) url += '&indirectOnly=true';
      if (searchExtra) url += searchExtra;
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        url
      );

      if (response.data.success) {
        setSuggestions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching employee suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, indirectOnly, searchExtra]);

  // Debounced search
  const handleInputChange = (e) => {
    const query = e.target.value;
    setInputValue(query);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query);
    }, 200);
  };

  // Handle selection
  const handleSelect = (employee) => {
    setInputValue(employee.emp_name);
    setIsOpen(false);
    setSuggestions([]);
    onChange({
      emp_code: employee.emp_code,
      emp_name: employee.emp_name,
      empl_code: employee.emp_code,
      empl_name: employee.emp_name,
      trade: employee.trade,
      division: employee.division,
      payroll_group: employee.payroll_group ?? null
    });
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear on blur if no selection made
  const handleBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      if (!value && inputValue) {
        // setInputValue('');
      }
    }, 200);
  };

  return (
    <div 
      ref={dropdownRef}
      style={{ 
        position: 'relative', 
        width: '100%',
        ...style 
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => inputValue && setIsOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          fontSize: '0.9rem',
          boxSizing: 'border-box',
          backgroundColor: disabled ? '#e9ecef' : 'white'
        }}
        autoComplete="off"
      />
      
      {loading && (
        <div style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '0.8rem',
          color: '#6c757d'
        }}>
          ...
        </div>
      )}

      {isOpen && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxHeight: '250px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          {suggestions.map((emp, index) => (
            <div
              key={emp.emp_code}
              onClick={() => handleSelect(emp)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                backgroundColor: highlightedIndex === index ? '#e9ecef' : 'white',
                borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div>
                <div style={{ fontWeight: '500', color: '#333' }}>
                  {emp.emp_name}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  {emp.trade && <span>{emp.trade}</span>}
                  {emp.trade && emp.division && <span> • </span>}
                  {emp.division && <span>{emp.division}</span>}
                </div>
              </div>
              <div style={{ 
                fontSize: '0.8rem', 
                color: '#007bff',
                fontFamily: 'monospace'
              }}>
                {emp.emp_code}
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpen && inputValue && suggestions.length === 0 && !loading && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '0 0 4px 4px',
          padding: '12px',
          color: '#6c757d',
          fontSize: '0.9rem',
          textAlign: 'center',
          zIndex: 1000
        }}>
          No employees found
        </div>
      )}
    </div>
  );
};

export default EmployeeAutocomplete;
